/**
 * Media with text video controls and lazy video modal.
 *
 * Keeps modal video markup inert until a trigger is activated, then removes the
 * instantiated player on close so subsequent opens remain deliberate.
 */
(function () {
  if (window.__mediaWithTextVideoModalInitialized) return;
  window.__mediaWithTextVideoModalInitialized = true;

  const initializedVideoMedia = new WeakSet();
  const initializedDialogs = new WeakSet();

  function playVideo(videoMedia, video) {
    if (typeof videoMedia.play === 'function') {
      videoMedia.play();
      return;
    }

    video.play().catch(() => {});
  }

  function pauseVideo(videoMedia, video) {
    if (typeof videoMedia.pause === 'function') {
      videoMedia.pause();
      return;
    }

    video.pause();
  }

  function initializeVideoControls(videoMedia) {
    if (initializedVideoMedia.has(videoMedia)) return;

    const video = videoMedia.querySelector('video');
    const controls = videoMedia.querySelector('[data-media-text-controls]');
    if (!video || !controls) return;

    initializedVideoMedia.add(videoMedia);

    const muteButton = controls.querySelector('[data-media-text-mute]');
    const unmuteButton = controls.querySelector('[data-media-text-unmute]');
    const playButton = controls.querySelector('[data-media-text-play]');
    const pauseButton = controls.querySelector('[data-media-text-pause]');

    function syncMuteButtons() {
      if (!muteButton || !unmuteButton) return;

      muteButton.classList.toggle('hidden', video.muted);
      unmuteButton.classList.toggle('hidden', !video.muted);
    }

    function syncPlayPauseButtons() {
      if (!playButton || !pauseButton) return;

      playButton.classList.toggle('hidden', !video.paused);
      pauseButton.classList.toggle('hidden', video.paused);
    }

    muteButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      video.muted = true;
      video.setAttribute('muted', '');
      syncMuteButtons();
    });

    unmuteButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      video.muted = false;
      video.removeAttribute('muted');
      video.volume = 1;

      if (video.paused) {
        playVideo(videoMedia, video);
      }

      syncMuteButtons();
    });

    playButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      playVideo(videoMedia, video);
    });

    pauseButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      pauseVideo(videoMedia, video);
    });

    video.addEventListener('play', () => {
      controls.classList.add('controls-revealed');
      syncPlayPauseButtons();
    });
    video.addEventListener('pause', syncPlayPauseButtons);
    video.addEventListener('volumechange', syncMuteButtons);

    syncMuteButtons();
    syncPlayPauseButtons();
  }

  function destroyDialogVideo(dialog) {
    const stage = dialog.querySelector('[data-media-text-modal-stage]');
    const video = stage?.querySelector('video');

    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.querySelectorAll('source').forEach((source) => source.remove());
      video.load();
    }

    stage?.replaceChildren();
  }

  function initializeDialog(dialog) {
    if (initializedDialogs.has(dialog)) return;
    initializedDialogs.add(dialog);

    let activeTrigger = null;
    let backdropPointerTarget = null;

    dialog.querySelector('[data-media-text-modal-close]')?.addEventListener('click', () => {
      dialog.close();
    });

    dialog.addEventListener('pointerdown', (event) => {
      backdropPointerTarget = event.target;
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog && backdropPointerTarget === dialog) {
        dialog.close();
      }
    });

    dialog.addEventListener('close', () => {
      destroyDialogVideo(dialog);

      if (activeTrigger) {
        activeTrigger.setAttribute('aria-expanded', 'false');
        activeTrigger.focus({ preventScroll: true });
        activeTrigger = null;
      }
    });

    dialog.openFromTrigger = function (trigger) {
      const stage = dialog.querySelector('[data-media-text-modal-stage]');
      const template = dialog.querySelector('[data-media-text-video-template]');
      if (!stage || !template || dialog.open) return;

      activeTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      stage.append(template.content.cloneNode(true));
      dialog.showModal();

      const videoMedia = stage.querySelector('video-media');
      const video = videoMedia?.querySelector('video');
      if (!videoMedia || !video) return;

      initializeVideoControls(videoMedia);
      video.load();
      playVideo(videoMedia, video);
    };
  }

  function initialize(root = document) {
    root.querySelectorAll('video-media.story-slide__video').forEach(initializeVideoControls);
    root.querySelectorAll('[data-media-text-video-dialog]').forEach(initializeDialog);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-media-text-modal-trigger]');
    if (!trigger) return;

    const dialogId = trigger.getAttribute('aria-controls');
    const dialog = dialogId ? document.getElementById(dialogId) : null;
    if (!dialog) return;

    initializeDialog(dialog);
    dialog.openFromTrigger(trigger);
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }
})();
